import { createContext, useContext, useEffect, useState } from 'react';
import api from '../api/client.js';

const AuthCtx = createContext(null);

const PERMISSION_DEFAULTS = {
  can_export:               { superadmin: true, dealer_admin: true, branch_admin: true,  sales: false },
  can_edit_customers:       { superadmin: true, dealer_admin: true, branch_admin: true,  sales: false },
  can_delete_opportunities: { superadmin: true, dealer_admin: true, branch_admin: true,  sales: false },
  can_access_user_admin:    { superadmin: true, dealer_admin: true, branch_admin: true,  sales: false },
};

function computePermission(user, perm) {
  if (!user) return false;
  const perms = user.permissions || {};
  if (typeof perms[perm] === 'boolean') return perms[perm];
  const defaults = PERMISSION_DEFAULTS[perm];
  if (!defaults) return user.role === 'superadmin';
  return defaults[user.role] ?? false;
}

const TOKEN_KEY = 'utsc_token';
// When impersonating, we stash the original superadmin token here so we can swap back.
const IMPERSONATOR_KEY = 'utsc_impersonator_token';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setLoading(false); return; }
    api.get('/auth/me')
      .then(r => setUser(r.data.data))
      .catch(() => localStorage.removeItem(TOKEN_KEY))
      .finally(() => setLoading(false));
  }, []);

  async function login(email, password) {
    const r = await api.post('/auth/login', { email, password });
    const { token, user } = r.data.data;
    localStorage.setItem(TOKEN_KEY, token);
    setUser(user);
    return user;
  }

  function logout() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(IMPERSONATOR_KEY);
    setUser(null);
  }

  /**
   * Swap to a target user's token. Stash the current (admin) token so we can
   * restore it when stopping. Caller must provide the new token + user.
   */
  function startImpersonation({ token, user: targetUser }) {
    const current = localStorage.getItem(TOKEN_KEY);
    if (current) localStorage.setItem(IMPERSONATOR_KEY, current);
    localStorage.setItem(TOKEN_KEY, token);
    setUser(targetUser);
  }

  /**
   * Notify the server (best-effort), restore the original admin token, refresh /me.
   */
  async function stopImpersonation() {
    try { await api.post('/admin/stop-impersonating'); } catch { /* best-effort */ }
    const original = localStorage.getItem(IMPERSONATOR_KEY);
    if (original) {
      localStorage.setItem(TOKEN_KEY, original);
      localStorage.removeItem(IMPERSONATOR_KEY);
      try {
        const r = await api.get('/auth/me');
        setUser(r.data.data);
      } catch {
        // Original token may have expired — force fresh login
        logout();
        window.location.href = '/login';
      }
    } else {
      logout();
      window.location.href = '/login';
    }
  }

  const isImpersonating = !!user?.impersonator;
  const hasPermission = (perm) => computePermission(user, perm);

  // Branch-level visibility gate. `branches` is a list of allowed branch codes
  // (e.g. ['FRESNO']). Superadmins always pass; otherwise the user's own branch
  // code must be in the list. An empty/undefined list means "no restriction".
  const hasBranchAccess = (branches) =>
    !branches || branches.length === 0 ||
    user?.role === 'superadmin' ||
    branches.includes(user?.branch_code);

  return (
    <AuthCtx.Provider value={{ user, loading, login, logout, setUser, startImpersonation, stopImpersonation, isImpersonating, hasPermission, hasBranchAccess }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const v = useContext(AuthCtx);
  if (!v) throw new Error('useAuth must be inside AuthProvider');
  return v;
}
